// Aircraft store — v2 schema with migration shim from v1.
//
// v1 (legacy) had a flat shape: { tail, vSpeeds: { cruiseKt, fuelBurnGph, ...},
// wb: { emptyWeight, ... } }. v2 is the ForeFlight-class profile defined in
// lib/aircraftTypes.ts. On load we sniff each saved record and migrate v1 →
// v2 once. Saves go out as v2.
//
// The downstream consumers (PlanPanel, FlightsPage, flightCalc) use a
// few well-known fields: `tail`, `cruise TAS`, `fuel burn`. v2 keeps
// those reachable via a typed accessor (`activeCruise()`).

import { create } from 'zustand';
import {
  AircraftProfile,
  PerformanceProfile,
  emptyAircraftProfile,
} from '../lib/aircraftTypes';
import { findPerformanceTemplate } from '../lib/performanceTemplates';
import {
  pushRow,
  pushMany,
  deleteRowByClientId,
  pullAll,
  type SyncBindings,
} from '../lib/syncCore';
import { useAuthStore } from './authStore';
import { newClientId } from '../lib/ids';

export type { AircraftProfile } from '../lib/aircraftTypes';

const KEY = 'fyrefly:aircraft:v1';
const SELECTED_KEY = 'fyrefly:aircraft:selected';

// ───────────────────── v1 legacy types (DO NOT EXPORT) ─────────────────────

interface LegacyV1 {
  id: string;
  tail: string;
  make?: string;
  model?: string;
  type?: string;
  vSpeeds?: {
    vS0?: number; vS1?: number; vR?: number; vX?: number; vY?: number;
    vFE?: number; vNO?: number; vNE?: number; vGlide?: number;
    cruiseKt?: number; fuelBurnGph?: number;
  };
  wb?: {
    emptyWeight?: number; emptyArm?: number; maxGross?: number;
    maxFuelGal?: number; fuelArm?: number; pilotPaxArm?: number;
    baggageArm?: number; cgForwardLimit?: number; cgAftLimit?: number;
  };
  notes?: string;
}

// ───────────────────── Migration ─────────────────────

function migrate(record: unknown): AircraftProfile {
  if (record && typeof record === 'object' && (record as { schemaVersion?: number }).schemaVersion === 2) {
    return record as AircraftProfile;
  }

  const v1 = record as LegacyV1;
  const blank = emptyAircraftProfile();

  const perf: PerformanceProfile[] = [];
  if (v1.vSpeeds?.cruiseKt || v1.vSpeeds?.fuelBurnGph) {
    perf.push({
      id: 'perf-default',
      name: 'Cruise',
      cruiseKtas: v1.vSpeeds?.cruiseKt ?? 0,
      fuelBurnGph: v1.vSpeeds?.fuelBurnGph ?? 0,
      isDefault: true,
    });
  }

  const profile: AircraftProfile = {
    ...blank,
    id: v1.id,
    tail: v1.tail || '',
    typeLabel: [v1.make, v1.model].filter(Boolean).join(' ') || undefined,
    notes: v1.notes,
    vSpeeds: {
      vS0: v1.vSpeeds?.vS0, vS1: v1.vSpeeds?.vS1, vR: v1.vSpeeds?.vR,
      vX: v1.vSpeeds?.vX, vY: v1.vSpeeds?.vY, vFE: v1.vSpeeds?.vFE,
      vNO: v1.vSpeeds?.vNO, vNE: v1.vSpeeds?.vNE, vGlide: v1.vSpeeds?.vGlide,
    },
    glide: { bestGlideKt: v1.vSpeeds?.vGlide },
    performanceProfiles: perf,
    basicEmptyLb: v1.wb?.emptyWeight,
    maxTakeoffLb: v1.wb?.maxGross,
    maxRampLb: v1.wb?.maxGross,
    maxLandingLb: v1.wb?.maxGross,
    fuel: {
      ...blank.fuel,
      totalUsable: v1.wb?.maxFuelGal ?? 0,
    },
  };

  // Seed a "Legacy" W&B profile from the old wb fields if we have
  // anything to work with. Only useful as a starting point — the
  // user MUST go in and either replace the envelope with their POH
  // numbers or pick a seed-by-type.
  if (v1.wb?.emptyWeight && v1.wb?.emptyArm && v1.wb?.cgForwardLimit && v1.wb?.cgAftLimit && v1.wb?.maxGross) {
    const fwd = v1.wb.cgForwardLimit;
    const aft = v1.wb.cgAftLimit;
    const mtow = v1.wb.maxGross;
    profile.wbProfiles = [{
      id: 'wb-legacy',
      name: 'Legacy (migrated)',
      emptyWeightLb: v1.wb.emptyWeight,
      emptyArmIn: v1.wb.emptyArm,
      stations: [
        ...(v1.wb.pilotPaxArm ? [{
          id: 'pilot', name: 'Pilot/Pax', armIn: v1.wb.pilotPaxArm, type: 'crew' as const,
        }] : []),
        ...(v1.wb.baggageArm ? [{
          id: 'bag', name: 'Baggage', armIn: v1.wb.baggageArm, type: 'baggage' as const,
        }] : []),
        ...(v1.wb.fuelArm && v1.wb.maxFuelGal ? [{
          id: 'fuel', name: 'Fuel', armIn: v1.wb.fuelArm, type: 'fuel' as const,
          capacityGal: v1.wb.maxFuelGal,
        }] : []),
      ],
      // Rectangular envelope from fwd-aft × empty-weight–MTOW.
      // It's a placeholder — the real envelope is rarely a rectangle —
      // but it lets the math work until the user enters real POH data.
      envelope: [
        { weightLb: v1.wb.emptyWeight, cgIn: fwd },
        { weightLb: mtow, cgIn: fwd },
        { weightLb: mtow, cgIn: aft },
        { weightLb: v1.wb.emptyWeight, cgIn: aft },
      ],
      fuelLbPerGal: 6.0,
      notes: 'Migrated from legacy profile. Replace envelope with real POH chart vertices for accuracy.',
    }];
    profile.activeWBProfileId = 'wb-legacy';
  }

  return profile;
}

// ───────────────────── Persistence ─────────────────────

function loadRaw(): AircraftProfile[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || '[]') as unknown[];
    return raw.map(migrate).map(backfillPerformanceTemplate);
  } catch {
    return [];
  }
}

/** Apply the POH performance template (takeoff + landing distance
 *  tables) onto an aircraft's default performance profile when the
 *  ICAO type matches a known template AND the profile doesn't already
 *  have its own digitized tables. Idempotent — if the user has
 *  customised either table, we leave them alone.
 *
 *  Used on:
 *   - localStorage load (boot)
 *   - cloud pull (sign-in / sync)
 *  so an existing aircraft (e.g. boss's N3405S, created before this
 *  feature shipped) inherits the matching template on next load.
 *
 *  Replaces an existing table when its `source` field still matches
 *  one of our shipped template-source strings. This lets us correct
 *  a wrong template (e.g. C182T → C182G shipped to boss's N3405S on
 *  2026-05-25 21:28 PT, then corrected at 23:32 PT when boss told us
 *  he flies a 1964 G-model) without clobbering tables a pilot has
 *  manually edited (their source string won't match ours). */
function backfillPerformanceTemplate(profile: AircraftProfile): AircraftProfile {
  const tpl = findPerformanceTemplate(profile.icaoType);
  if (!tpl) return profile;
  const profiles = profile.performanceProfiles ?? [];
  if (profiles.length === 0) return profile;
  let changed = false;
  const next = profiles.map((p) => {
    if (!p.isDefault) return p;
    let np: PerformanceProfile = p;
    if (!p.takeoffDistance || isStockTemplateSource(p.takeoffDistance.source)) {
      np = { ...np, takeoffDistance: tpl.takeoff };
      changed = true;
    }
    if (!np.landingDistance || isStockTemplateSource(np.landingDistance.source)) {
      np = { ...np, landingDistance: tpl.landing };
      changed = true;
    }
    return np;
  });
  return changed ? { ...profile, performanceProfiles: next } : profile;
}

/** Returns true if a PerformanceDistanceTable.source string came from
 *  one of our shipped templates (i.e. has NOT been pilot-edited). The
 *  check is used to decide whether the backfill is allowed to replace
 *  the table. Add new prefixes here when shipping new template
 *  sources so they're treated as replaceable seeds. */
function isStockTemplateSource(src: string | undefined): boolean {
  if (!src) return false;
  return (
    src.startsWith('POH ') ||                       // PA-28, SR22, SR20, BE36, C172
    src.startsWith('Cessna 1964 182G Owner') ||     // current C182 template
    src.startsWith('POH C182T ')                    // legacy C182T template, replaced by 182G
  );
}

function save(p: AircraftProfile[]) {
  localStorage.setItem(KEY, JSON.stringify(p));
}

// ───────────────────── Store ─────────────────────

interface AircraftDbRow {
  owner_id: string;
  org_id: string | null;
  client_id: string;
  tail: string | null;
  type_label: string | null;
  category: string | null;
  is_default: boolean;
  profile: AircraftProfile;
}

const AIRCRAFT_SYNC: SyncBindings<AircraftProfile, AircraftDbRow> = {
  tableName: 'aircraft',
  upsertConflict: 'owner_id,client_id',
  localIdField: 'id',
  toDbRow(p, ownerId) {
    return {
      owner_id: ownerId,
      org_id: p.orgId ?? null,
      client_id: p.id,
      tail: p.tail ?? null,
      type_label: p.typeLabel ?? null,
      category: (p.category as unknown as string) ?? null,
      is_default: !!p.isDefault,
      profile: p,
    };
  },
  fromDbRow(row) {
    // Server-side `profile` jsonb is the full client shape; trust it
    // and just patch the top-level convenience fields the table
    // also stores so they win on conflict.
    const profile = (row.profile as AircraftProfile) ?? emptyAircraftProfile();
    return {
      ...profile,
      id: row.client_id ?? (profile as AircraftProfile).id,
      orgId: row.org_id ?? profile.orgId ?? null,
      tail: row.tail ?? profile.tail ?? '',
      typeLabel: row.type_label ?? profile.typeLabel,
      isDefault: !!row.is_default,
    };
  },
};

export interface AircraftState {
  list: AircraftProfile[];
  selectedId: string | null;
  syncing: boolean;
  syncError: string | null;
  add: (p: Omit<AircraftProfile, 'id'>) => string;
  update: (id: string, patch: Partial<AircraftProfile>) => void;
  /** Deep-merge update for a nested key (e.g. fuel, filing, glide). */
  patchNested: <K extends keyof AircraftProfile>(
    id: string, key: K, patch: Partial<NonNullable<AircraftProfile[K]>>,
  ) => void;
  remove: (id: string) => void;
  duplicate: (id: string) => string | null;
  select: (id: string | null) => void;
  setDefault: (id: string) => void;
  /** Move an aircraft to an organization (every member sees it) or
   *  back to personal (pass null). The aircraft's owner_id stays the
   *  same; only the org_id changes. */
  setOrg: (id: string, orgId: string | null) => void;
  syncFromServer: () => Promise<void>;
  syncToServer: () => Promise<void>;
}

function pushAircraftRow(p: AircraftProfile, set: (s: Partial<AircraftState>) => void) {
  void pushRow(p, AIRCRAFT_SYNC).then((r) => {
    if (!r.ok && r.error) set({ syncError: r.error });
    else if (r.ok) set({ syncError: null });
  });
}
function pushAircraftMany(rows: AircraftProfile[], set: (s: Partial<AircraftState>) => void) {
  void pushMany(rows, AIRCRAFT_SYNC).then((r) => {
    if (!r.ok && r.error) set({ syncError: r.error });
    else if (r.ok) set({ syncError: null });
  });
}
function deleteAircraftRow(id: string, set: (s: Partial<AircraftState>) => void) {
  void deleteRowByClientId(id, AIRCRAFT_SYNC).then((r) => {
    if (!r.ok && r.error) set({ syncError: r.error });
  });
}

export const useAircraftStore = create<AircraftState>((set, get) => ({
  list: loadRaw(),
  selectedId: localStorage.getItem(SELECTED_KEY) || null,
  syncing: false,
  syncError: null,

  add: (p) => {
    const id = newClientId('ac');
    const created = { ...p, id } as AircraftProfile;
    const next = [...get().list, created];
    save(next);
    set({ list: next, selectedId: get().selectedId ?? id });
    pushAircraftRow(created, (s) => set(s));
    return id;
  },

  update: (id, patch) => {
    const next = get().list.map((a) => (a.id === id ? { ...a, ...patch } : a));
    save(next);
    set({ list: next });
    const updated = next.find((a) => a.id === id);
    if (updated) pushAircraftRow(updated, (s) => set(s));
  },

  patchNested: (id, key, patch) => {
    const next = get().list.map((a) => {
      if (a.id !== id) return a;
      const cur = a[key] as Record<string, unknown> | undefined;
      return { ...a, [key]: { ...(cur ?? {}), ...patch } };
    });
    save(next);
    set({ list: next });
    const updated = next.find((a) => a.id === id);
    if (updated) pushAircraftRow(updated, (s) => set(s));
  },

  remove: (id) => {
    const next = get().list.filter((a) => a.id !== id);
    save(next);
    let sel = get().selectedId;
    if (sel === id) {
      sel = null;
      localStorage.removeItem(SELECTED_KEY);
    }
    set({ list: next, selectedId: sel });
    deleteAircraftRow(id, (s) => set(s));
  },

  duplicate: (id) => {
    const src = get().list.find((a) => a.id === id);
    if (!src) return null;
    const newId = newClientId('ac');
    const copy: AircraftProfile = {
      ...JSON.parse(JSON.stringify(src)),
      id: newId,
      tail: `${src.tail || 'NEW'} (copy)`,
      isDefault: false,
    };
    const next = [...get().list, copy];
    save(next);
    set({ list: next });
    pushAircraftRow(copy, (s) => set(s));
    return newId;
  },

  select: (id) => {
    if (id) localStorage.setItem(SELECTED_KEY, id);
    else localStorage.removeItem(SELECTED_KEY);
    set({ selectedId: id });
  },

  setDefault: (id) => {
    const next = get().list.map((a) => ({ ...a, isDefault: a.id === id }));
    save(next);
    set({ list: next });
    // Push the whole list — partial unique index on the server needs
    // the previous default cleared in the same statement.
    pushAircraftMany(next, (s) => set(s));
  },

  setOrg: (id, orgId) => {
    const next = get().list.map((a) => (a.id === id ? { ...a, orgId } : a));
    save(next);
    set({ list: next });
    const moved = next.find((a) => a.id === id);
    if (moved) pushAircraftRow(moved, (s) => set(s));
  },

  syncFromServer: async () => {
    set({ syncing: true });
    try {
      // First push any local rows so a fresh sign-in doesn't lose data.
      const local = get().list;
      if (local.length > 0) {
        const r = await pushMany(local, AIRCRAFT_SYNC);
        if (!r.ok && r.error) {
          set({ syncing: false, syncError: r.error });
          return;
        }
      }
      const r = await pullAll(AIRCRAFT_SYNC);
      if (!r.ok) {
        set({ syncing: false, syncError: r.error ?? null });
        return;
      }
      const seeded = r.rows.map(backfillPerformanceTemplate);
      save(seeded);
      set({ list: seeded, syncing: false, syncError: null });
    } catch (e: any) {
      set({ syncing: false, syncError: e?.message ?? String(e) });
    }
  },

  syncToServer: async () => {
    set({ syncing: true });
    const r = await pushMany(get().list, AIRCRAFT_SYNC);
    set({ syncing: false, syncError: r.ok ? null : r.error ?? null });
  },
}));

// Re-sync whenever a session appears.
useAuthStore.subscribe((state, prev) => {
  const wasIn = !!prev.user;
  const isIn = !!state.user;
  if (!wasIn && isIn) {
    void useAircraftStore.getState().syncFromServer();
  }
});

// ───────────────────── Helpers used by downstream code ─────────────────────

/**
 * Resolve the aircraft to use for flight-ops surfaces (instrument bar,
 * glide ring, Ruler, plan calc, etc.). Priority:
 *   1. The profile flagged `isDefault: true` — user-explicit pick.
 *   2. The selected (currently-editing) profile.
 *   3. The first profile in the list — lets a single-aircraft user
 *      get flight-ops features without any extra setup step.
 *   4. null when the list is empty.
 *
 * The legacy `selectedId` slot was only ever the edit pointer in the
 * /aircraft page; flight-ops code that read it was effectively asking
 * "which aircraft did you last open in the editor?" — wrong question.
 */
export function pickActiveAircraft(
  list: AircraftProfile[],
  selectedId: string | null,
): AircraftProfile | null {
  if (list.length === 0) return null;
  const def = list.find((a) => a.isDefault);
  if (def) return def;
  if (selectedId) {
    const sel = list.find((a) => a.id === selectedId);
    if (sel) return sel;
  }
  return list[0];
}

/** zustand selector for the resolved active aircraft. */
export function selectActiveAircraft(s: AircraftState): AircraftProfile | null {
  return pickActiveAircraft(s.list, s.selectedId);
}

/** The default cruise TAS (knots) and fuel burn (gph) for use by
 *  flightCalc. Pulls from the active performance profile, falling back
 *  to the first profile, then to undefined if none defined yet. */
export function activeCruise(
  a: AircraftProfile | null | undefined,
  /** Optional explicit profile id (e.g. from the active plan store).
   *  Falls back to the default-flagged profile, then the first
   *  profile, then undefined. Added 2026-05-17 so the PlanPanel
   *  performance-profile picker actually drives the calc. */
  performanceProfileId?: string | null,
): {
  cruiseKt: number | undefined;
  fuelBurnGph: number | undefined;
} {
  if (!a) return { cruiseKt: undefined, fuelBurnGph: undefined };
  let chosen = performanceProfileId
    ? a.performanceProfiles.find((p) => p.id === performanceProfileId)
    : null;
  if (!chosen) {
    chosen = a.performanceProfiles.find((p) => p.isDefault)
          ?? a.performanceProfiles[0];
  }
  return {
    cruiseKt: chosen?.cruiseKtas,
    fuelBurnGph: chosen?.fuelBurnGph,
  };
}
